import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon, EyeIcon, EyeOffIcon, XCircle, ArrowLeftIcon, ListIcon, FileTextIcon, CalendarIcon, ChevronDownIcon, ChevronUpIcon, EditIcon, CheckIcon, ClockIcon, PaperclipIcon, UploadIcon, DownloadIcon, Trash2Icon, FileIcon, Loader2Icon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { RichTextEditor } from '../components/RichTextEditor';
import { Customer } from '../components/CustomerCard';
import { Task, TaskAttachment } from '../components/TaskCard';
import { doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
interface TaskDetailProps {
  task: Task;
  customers: Customer[];
  onBack: () => void;
  onSave: (updatedTask: Task) => void;
  isReadOnly?: boolean;
}
export function TaskDetail({
  task,
  customers,
  onBack,
  onSave,
  isReadOnly = false
}: TaskDetailProps) {
  const [title, setTitle] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'todo' | 'in-progress' | 'done' | 'planned' | 'pending'>('in-progress');
  const [chargerType, setChargerType] = useState<'Zaptec 1' | 'Zaptec 2' | 'Andet'>('Zaptec 1');
  const [taskType, setTaskType] = useState<'Tilbudsopgave' | 'Regningsopgave'>('Tilbudsopgave');
  const [agreedPlanning, setAgreedPlanning] = useState('');
  const [agreedPlanningTime, setAgreedPlanningTime] = useState('09:00');
  const [agreedInstallation, setAgreedInstallation] = useState('');
  const [agreedInstallationTime, setAgreedInstallationTime] = useState('09:00');
  const [carDeliveryDate, setCarDeliveryDate] = useState('');
  const [hours, setHours] = useState('');
  const [materialPrice, setMaterialPrice] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [quotePrice, setQuotePrice] = useState('');
  const [invoiced, setInvoiced] = useState('no');
  const [subTasks, setSubTasks] = useState('');
  const [materialInput, setMaterialInput] = useState('');
  const [materialPriceInput, setMaterialPriceInput] = useState('');
  const [materials, setMaterials] = useState<string[]>([]);
  const [showMaterials, setShowMaterials] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkInput, setBulkInput] = useState('');
  const [bulkPrice, setBulkPrice] = useState('');
  const [editingMaterialIndex, setEditingMaterialIndex] = useState<number | null>(null);
  const [editingMaterialValue, setEditingMaterialValue] = useState('');
  const [workLogDate, setWorkLogDate] = useState(new Date().toISOString().split('T')[0]);
  const [workLogDescription, setWorkLogDescription] = useState('');
  const [workLogHours, setWorkLogHours] = useState('');
  const [workLog, setWorkLog] = useState<Array<{
    date: string;
    description: string;
    hours?: number;
  }>>([]);
  const [showWorkLog, setShowWorkLog] = useState(false);
  const [editingWorkLogIndex, setEditingWorkLogIndex] = useState<number | null>(null);
  const [editingWorkLogDate, setEditingWorkLogDate] = useState('');
  const [editingWorkLogDescription, setEditingWorkLogDescription] = useState('');
  const [editingWorkLogHours, setEditingWorkLogHours] = useState('');
  // Attachments state
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    pricing: true,
    materials: true,
    workLog: true,
    attachments: true
  });
  const [chargerPrices, setChargerPrices] = useState({
    'Zaptec 1': 1046.3,
    'Zaptec 2': 4321.54,
    Andet: 0
  });
  useEffect(() => {
    const loadPrices = async () => {
      try {
        const docRef = doc(db, 'settings', 'chargerPrices');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) setChargerPrices(docSnap.data() as any);
      } catch (error) {
        console.error('Error loading charger prices:', error);
      }
    };
    loadPrices();
  }, []);
  useEffect(() => {
    if (task?.category === 'Ladeboks' && chargerType !== 'Andet') {
      setMaterialPrice(chargerPrices[chargerType].toString());
    }
  }, [chargerType, task?.category, chargerPrices]);
  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setCustomerId(task.customerId);
      setDescription(task.description || '');
      setStatus(task.status);
      setChargerType(task.chargerType || 'Zaptec 1');
      setTaskType(task.taskType || 'Tilbudsopgave');
      if (task.agreedPlanning) {
        const [date, time] = task.agreedPlanning.split('T');
        setAgreedPlanning(date);
        setAgreedPlanningTime(time ? time.substring(0, 5) : '09:00');
      } else {
        setAgreedPlanning('');
        setAgreedPlanningTime('09:00');
      }
      if (task.agreedInstallation) {
        const [date, time] = task.agreedInstallation.split('T');
        setAgreedInstallation(date);
        setAgreedInstallationTime(time ? time.substring(0, 5) : '09:00');
      } else {
        setAgreedInstallation('');
        setAgreedInstallationTime('09:00');
      }
      setCarDeliveryDate(task.carDeliveryDate || '');
      setHours(task.hours?.toString() || '');
      setMaterialPrice(task.materialPrice?.toString() || '');
      setHourlyRate(task.hourlyRate?.toString() || '');
      setQuotePrice(task.quotePrice?.toString() || '');
      setInvoiced(task.invoiced ? 'yes' : 'no');
      setSubTasks(task.subTasks || '');
      setMaterials(task.materials || []);
      setMaterialInput('');
      setMaterialPriceInput('');
      setBulkInput('');
      setBulkPrice('');
      setShowMaterials(false);
      setBulkMode(false);
      setEditingMaterialIndex(null);
      setEditingMaterialValue('');
      setWorkLog(task.workLog || []);
      setWorkLogDate(new Date().toISOString().split('T')[0]);
      setWorkLogDescription('');
      setWorkLogHours('');
      setShowWorkLog(false);
      setEditingWorkLogIndex(null);
      setEditingWorkLogDate('');
      setEditingWorkLogDescription('');
      setEditingWorkLogHours('');
      setAttachments(task.attachments || []);
    }
  }, [task]);
  const totalHoursWithWorkLog = useMemo(() => {
    const baseHours = hours ? parseFloat(hours) : 0;
    const workLogHoursTotal = workLog.reduce((sum, entry) => sum + (entry.hours || 0), 0);
    return baseHours + workLogHoursTotal;
  }, [hours, workLog]);
  const calculations = useMemo(() => {
    const material = materialPrice ? parseFloat(materialPrice) : 0;
    const rate = hourlyRate ? parseFloat(hourlyRate) : 0;
    const time = totalHoursWithWorkLog;
    const quote = quotePrice ? parseFloat(quotePrice) : 0;
    if (taskType === 'Tilbudsopgave') {
      const profit = quote - material;
      const totalLaborCost = rate * time;
      return {
        totalPrice: quote,
        profit: quote > 0 || material > 0 ? profit : null,
        totalLaborCost: rate > 0 && time > 0 ? totalLaborCost : null
      };
    } else {
      const laborCost = rate * time;
      const totalPrice = laborCost + material;
      const profit = laborCost - material;
      return {
        totalPrice,
        profit: rate > 0 && time > 0 || material > 0 ? profit : null,
        totalLaborCost: rate > 0 && time > 0 ? laborCost : null
      };
    }
  }, [taskType, quotePrice, materialPrice, hourlyRate, totalHoursWithWorkLog]);
  const handleAddMaterial = () => {
    if (materialInput.trim()) {
      setMaterials([...materials, materialInput.trim()]);
      if (materialPriceInput.trim()) {
        const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
        const additionalPrice = parseFloat(materialPriceInput);
        setMaterialPrice((currentPrice + additionalPrice).toString());
      }
      setMaterialInput('');
      setMaterialPriceInput('');
    }
  };
  const handleAddBulkMaterials = () => {
    if (bulkInput.trim()) {
      const newMaterials = bulkInput.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      if (newMaterials.length > 0) {
        setMaterials([...materials, ...newMaterials]);
        if (bulkPrice.trim()) {
          const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
          setMaterialPrice((currentPrice + parseFloat(bulkPrice)).toString());
        }
        setBulkInput('');
        setBulkPrice('');
        setBulkMode(false);
      }
    }
  };
  const handleRemoveMaterial = (index: number) => setMaterials(materials.filter((_, i) => i !== index));
  const handleStartEditMaterial = (index: number) => {
    setEditingMaterialIndex(index);
    setEditingMaterialValue(materials[index]);
  };
  const handleSaveEditMaterial = () => {
    if (editingMaterialIndex !== null && editingMaterialValue.trim()) {
      const updatedMaterials = [...materials];
      updatedMaterials[editingMaterialIndex] = editingMaterialValue.trim();
      setMaterials(updatedMaterials);
      setEditingMaterialIndex(null);
      setEditingMaterialValue('');
    }
  };
  const handleCancelEditMaterial = () => {
    setEditingMaterialIndex(null);
    setEditingMaterialValue('');
  };
  const handleAddWorkLog = () => {
    if (workLogDescription.trim() && workLogDate) {
      const entry: any = {
        date: workLogDate,
        description: workLogDescription.trim()
      };
      if (workLogHours.trim()) entry.hours = parseFloat(workLogHours);
      setWorkLog([...workLog, entry]);
      setWorkLogDescription('');
      setWorkLogHours('');
      setWorkLogDate(new Date().toISOString().split('T')[0]);
    }
  };
  const handleRemoveWorkLog = (index: number) => setWorkLog(workLog.filter((_, i) => i !== index));
  const handleStartEditWorkLog = (index: number) => {
    const entry = workLog[index];
    setEditingWorkLogIndex(index);
    setEditingWorkLogDate(entry.date);
    setEditingWorkLogDescription(entry.description);
    setEditingWorkLogHours(entry.hours?.toString() || '');
  };
  const handleSaveEditWorkLog = () => {
    if (editingWorkLogIndex !== null && editingWorkLogDescription.trim() && editingWorkLogDate) {
      const updatedWorkLog = [...workLog];
      const entry: any = {
        date: editingWorkLogDate,
        description: editingWorkLogDescription.trim()
      };
      if (editingWorkLogHours.trim()) entry.hours = parseFloat(editingWorkLogHours);
      updatedWorkLog[editingWorkLogIndex] = entry;
      setWorkLog(updatedWorkLog);
      setEditingWorkLogIndex(null);
      setEditingWorkLogDate('');
      setEditingWorkLogDescription('');
      setEditingWorkLogHours('');
    }
  };
  const handleCancelEditWorkLog = () => {
    setEditingWorkLogIndex(null);
    setEditingWorkLogDate('');
    setEditingWorkLogDescription('');
    setEditingWorkLogHours('');
  };
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !task) return;
    try {
      setIsUploading(true);
      const storagePath = `tasks/${task.id}/bilag/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const newAttachment: TaskAttachment = {
        name: file.name,
        url,
        storagePath,
        uploadedAt: new Date().toISOString(),
        size: file.size
      };
      setAttachments(prev => [...prev, newAttachment]);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Der opstod en fejl under upload af filen.');
    } finally {
      setIsUploading(false);
      // Reset input
      e.target.value = '';
    }
  };
  const handleDeleteAttachment = async (attachment: TaskAttachment, index: number) => {
    if (!confirm('Er du sikker på, at du vil slette dette bilag?')) return;
    try {
      const storageRef = ref(storage, attachment.storagePath);
      await deleteObject(storageRef);
      setAttachments(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Der opstod en fejl under sletning af filen.');
    }
  };
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';else return (bytes / 1048576).toFixed(1) + ' MB';
  };
  const toggleAllSections = () => {
    const allExpanded = expandedSections.pricing && expandedSections.materials && expandedSections.workLog && expandedSections.attachments;
    setExpandedSections({
      pricing: !allExpanded,
      materials: !allExpanded,
      workLog: !allExpanded,
      attachments: !allExpanded
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!task || !title.trim() || !customerId) return;
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return;
    const planningDateTime = agreedPlanning ? `${agreedPlanning}T${agreedPlanningTime}:00` : undefined;
    const installationDateTime = agreedInstallation ? `${agreedInstallation}T${agreedInstallationTime}:00` : undefined;
    onSave({
      ...task,
      title,
      customerId,
      customerName: customer.name,
      description,
      status,
      chargerType: task.category === 'Ladeboks' ? chargerType : undefined,
      taskType,
      agreedPlanning: planningDateTime,
      agreedInstallation: installationDateTime,
      carDeliveryDate: carDeliveryDate || undefined,
      hours: hours ? parseFloat(hours) : undefined,
      materialPrice: materialPrice ? parseFloat(materialPrice) : undefined,
      hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
      quotePrice: quotePrice ? parseFloat(quotePrice) : undefined,
      totalPrice: calculations.totalPrice || undefined,
      invoiced: invoiced === 'yes',
      subTasks: subTasks || undefined,
      materials: materials.length > 0 ? materials : undefined,
      workLog: workLog.length > 0 ? workLog : undefined,
      attachments: attachments.length > 0 ? attachments : undefined
    });
    onBack();
  };
  if (!task) return null;
  const allExpanded = expandedSections.pricing && expandedSections.materials && expandedSections.workLog && expandedSections.attachments;
  return <div className="w-full" data-id="element-1892">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      type: 'spring',
      duration: 0.3
    }} className="max-w-4xl mx-auto" data-id="element-1893">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200" data-id="element-1894">
          {/* Sticky header */}
          <div className="sticky top-0 z-10 bg-white border-b border-stone-200 rounded-t-2xl px-4 sm:px-6 py-4" data-id="element-1895">
            <div className="flex items-center justify-between" data-id="element-1896">
              <div className="flex items-center gap-4" data-id="element-1897">
                <button onClick={onBack} className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-colors" aria-label="Tilbage" data-id="element-1898">
                  <ArrowLeftIcon className="w-6 h-6" data-id="element-1899" />
                </button>
                <div data-id="element-1900">
                  <h2 className="text-xl sm:text-2xl font-semibold text-stone-900" data-id="element-1901">
                    Rediger opgave
                  </h2>
                  {task?.taskId && <p className="text-sm text-stone-500 mt-0.5" data-id="element-1902">
                      Opgave-ID:{' '}
                      <span className="font-medium text-teal-600" data-id="element-1903">
                        {task.taskId}
                      </span>
                    </p>}
                </div>
              </div>
              {!isReadOnly && <Button type="button" variant="primary" onClick={handleSubmit as any} className="hidden sm:flex" data-id="element-1904">
                  Gem ændringer
                </Button>}
            </div>
          </div>

          <div className="p-4 sm:p-6" data-id="element-1905">
            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1906">
              {/* Basic Information */}
              <div data-id="element-1907">
                <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-1908">
                  Grundlæggende information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1909">
                  <Input label="Opgavetitel" value={title} onChange={e => setTitle(e.target.value)} placeholder="Indtast opgavetitel" required disabled={isReadOnly} data-id="element-1910" />

                  <Select label="Kunde" value={customerId} onChange={e => setCustomerId(e.target.value)} options={[{
                  value: '',
                  label: 'Vælg en kunde'
                }, ...customers.map(c => ({
                  value: c.id,
                  label: `${c.name} (${c.customerId})`
                }))]} required disabled={isReadOnly} data-id="element-1911" />
                </div>

                <div className="mt-4" data-id="element-1912">
                  <RichTextEditor label="Beskrivelse" value={description} onChange={setDescription} placeholder="Beskriv opgaven" rows={3} disabled={isReadOnly} data-id="element-1913" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-1914">
                  <Select label="Status" value={status} onChange={e => setStatus(e.target.value as 'todo' | 'in-progress' | 'done' | 'planned' | 'pending')} options={[{
                  value: 'planned',
                  label: 'Planlagt'
                }, {
                  value: 'pending',
                  label: 'Afventende'
                }, {
                  value: 'in-progress',
                  label: 'I gang'
                }, {
                  value: 'done',
                  label: 'Afsluttet'
                }]} disabled={isReadOnly} data-id="element-1915" />

                  <Select label="Faktureret" value={invoiced} onChange={e => setInvoiced(e.target.value)} options={[{
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'yes',
                  label: 'Ja'
                }]} disabled={isReadOnly} data-id="element-1916" />
                </div>
              </div>

              {/* Dates */}
              <div data-id="element-1917">
                <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-1918">
                  Datoer
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1919">
                  <div data-id="element-1920">
                    <Input label="Aftalt projektering" type="date" value={agreedPlanning} onChange={e => setAgreedPlanning(e.target.value)} disabled={isReadOnly} data-id="element-1921" />
                    {agreedPlanning && <div className="mt-2" data-id="element-1922">
                        <Input label="Tidspunkt" type="time" value={agreedPlanningTime} onChange={e => setAgreedPlanningTime(e.target.value)} disabled={isReadOnly} data-id="element-1923" />
                      </div>}
                  </div>

                  <div data-id="element-1924">
                    <Input label="Aftalt installation" type="date" value={agreedInstallation} onChange={e => setAgreedInstallation(e.target.value)} disabled={isReadOnly} data-id="element-1925" />
                    {agreedInstallation && <div className="mt-2" data-id="element-1926">
                        <Input label="Tidspunkt" type="time" value={agreedInstallationTime} onChange={e => setAgreedInstallationTime(e.target.value)} disabled={isReadOnly} data-id="element-1927" />
                      </div>}
                  </div>
                </div>

                {task.category === 'Ladeboks' && <div className="mt-4" data-id="element-1928">
                    <Input label="Leveringsdato på bil (valgfrit)" type="date" value={carDeliveryDate} onChange={e => setCarDeliveryDate(e.target.value)} disabled={isReadOnly} data-id="element-1929" />
                    <p className="text-xs text-stone-500 mt-1" data-id="element-1930">
                      Angiv hvornår bilen leveres til kunden
                    </p>
                  </div>}
              </div>

              {/* Expand/Collapse All Button */}
              {!isReadOnly && <div className="flex justify-end" data-id="element-1931">
                  <button type="button" onClick={toggleAllSections} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-700 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors" data-id="element-1932">
                    {allExpanded ? <>
                        <ChevronUpIcon className="w-4 h-4" data-id="element-1933" />
                        Skjul alle sektioner
                      </> : <>
                        <ChevronDownIcon className="w-4 h-4" data-id="element-1934" />
                        Udvid alle sektioner
                      </>}
                  </button>
                </div>}

              {/* PRICING SECTION */}
              <AnimatePresence data-id="element-1935">
                {expandedSections.pricing ? <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-1936">
                    <div data-id="element-1937">
                      <button type="button" onClick={() => setExpandedSections({
                    ...expandedSections,
                    pricing: false
                  })} className="flex items-center justify-between w-full mb-4 group" disabled={isReadOnly} data-id="element-1938">
                        <h3 className="text-lg font-semibold text-stone-900" data-id="element-1939">
                          Priser og timer
                        </h3>
                        <div className="flex items-center gap-2" data-id="element-1940">
                          {totalHoursWithWorkLog > 0 && <span className="text-sm text-stone-500" data-id="element-1941">
                              Total: {totalHoursWithWorkLog.toFixed(1)}t
                            </span>}
                          <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-1942" />
                        </div>
                      </button>

                      <div className="space-y-4" data-id="element-1943">
                        {task.category === 'Ladeboks' && <Select label="Ladeboks type" value={chargerType} onChange={e => setChargerType(e.target.value as 'Zaptec 1' | 'Zaptec 2' | 'Andet')} options={[{
                      value: 'Zaptec 1',
                      label: 'Zaptec 1'
                    }, {
                      value: 'Zaptec 2',
                      label: 'Zaptec 2'
                    }, {
                      value: 'Andet',
                      label: 'Andet'
                    }]} required disabled={isReadOnly} data-id="element-1944" />}

                        <Select label="Opgavetype" value={taskType} onChange={e => setTaskType(e.target.value as 'Tilbudsopgave' | 'Regningsopgave')} options={[{
                      value: 'Tilbudsopgave',
                      label: 'Tilbudsopgave'
                    }, {
                      value: 'Regningsopgave',
                      label: 'Regningsopgave'
                    }]} required disabled={isReadOnly} data-id="element-1945" />

                        {taskType === 'Tilbudsopgave' ? <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1946">
                              <Input label="Tilbudspris (kr)" type="number" step="0.01" value={quotePrice} onChange={e => setQuotePrice(e.target.value)} placeholder="f.eks. 8000" required disabled={isReadOnly} data-id="element-1947" />
                              <Input label="Materialer pris (kr)" type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={isReadOnly || task.category === 'Ladeboks' && chargerType !== 'Andet'} data-id="element-1948" />
                            </div>

                            {task.category === 'Ladeboks' && chargerType !== 'Andet' && <p className="text-xs text-stone-500 mt-2" data-id="element-1949">
                                  Materialer pris udfyldes automatisk baseret på
                                  ladeboks type
                                </p>}

                            <div className="mt-4" data-id="element-1950">
                              <Input label="Timepris (kr)" type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="f.eks. 750" disabled={isReadOnly} data-id="element-1951" />
                            </div>

                            <div className="mt-4" data-id="element-1952">
                              <Input label="Timer brugt" type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" disabled={isReadOnly} data-id="element-1953" />
                            </div>

                            <div className="mt-4 space-y-3" data-id="element-1954">
                              {calculations.profit !== null && <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg" data-id="element-1955">
                                  <div className="flex items-center justify-between" data-id="element-1956">
                                    <span className="text-sm font-medium text-teal-900" data-id="element-1957">
                                      Fortjeneste
                                    </span>
                                    <span className={`text-lg font-bold ${calculations.profit >= 0 ? 'text-teal-700' : 'text-red-700'}`} data-id="element-1958">
                                      {calculations.profit.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                      kr
                                    </span>
                                  </div>
                                </div>}

                              {calculations.totalLaborCost !== null && calculations.totalLaborCost !== undefined && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1959">
                                    <div className="flex items-center justify-between" data-id="element-1960">
                                      <span className="text-sm font-medium text-blue-900" data-id="element-1961">
                                        Samlet pris på timer
                                      </span>
                                      <span className="text-lg font-bold text-blue-700" data-id="element-1962">
                                        {calculations.totalLaborCost.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                    <p className="text-xs text-blue-600 mt-1" data-id="element-1963">
                                      Timepris × Timer brugt
                                    </p>
                                  </div>}
                            </div>
                          </> : <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1964">
                              <Input label="Timepris (kr)" type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="f.eks. 750" required disabled={isReadOnly} data-id="element-1965" />
                              <Input label="Timer brugt" type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" required disabled={isReadOnly} data-id="element-1966" />
                              <Input label="Materialer pris (kr)" type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={isReadOnly || task.category === 'Ladeboks' && chargerType !== 'Andet'} data-id="element-1967" />
                            </div>

                            {task.category === 'Ladeboks' && chargerType !== 'Andet' && <p className="text-xs text-stone-500 mt-2" data-id="element-1968">
                                  Materialer pris udfyldes automatisk baseret på
                                  ladeboks type
                                </p>}

                            <div className="mt-4 space-y-3" data-id="element-1969">
                              {calculations.totalLaborCost !== null && calculations.totalLaborCost !== undefined && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1970">
                                    <div className="flex items-center justify-between" data-id="element-1971">
                                      <span className="text-sm font-medium text-blue-900" data-id="element-1972">
                                        Samlet pris på timer
                                      </span>
                                      <span className="text-lg font-bold text-blue-700" data-id="element-1973">
                                        {calculations.totalLaborCost.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                    <p className="text-xs text-blue-600 mt-1" data-id="element-1974">
                                      Timepris × Timer brugt
                                    </p>
                                  </div>}

                              <div className="p-4 bg-stone-50 border border-stone-200 rounded-lg" data-id="element-1975">
                                <div className="flex items-center justify-between" data-id="element-1976">
                                  <span className="text-sm font-medium text-stone-700" data-id="element-1977">
                                    Samlet pris
                                  </span>
                                  <span className="text-lg font-bold text-stone-900" data-id="element-1978">
                                    {calculations.totalPrice.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                    kr
                                  </span>
                                </div>
                              </div>

                              {calculations.profit !== null && <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg" data-id="element-1979">
                                  <div className="flex items-center justify-between" data-id="element-1980">
                                    <span className="text-sm font-medium text-teal-900" data-id="element-1981">
                                      Fortjeneste
                                    </span>
                                    <span className={`text-lg font-bold ${calculations.profit >= 0 ? 'text-teal-700' : 'text-red-700'}`} data-id="element-1982">
                                      {calculations.profit.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                      kr
                                    </span>
                                  </div>
                                </div>}
                            </div>
                          </>}
                      </div>
                    </div>
                  </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-1983">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  pricing: true
                })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-1984">
                      <ChevronDownIcon className="w-4 h-4" data-id="element-1985" />
                      Vis Priser og timer
                      {totalHoursWithWorkLog > 0 && <span className="text-stone-500" data-id="element-1986">
                          (Total: {totalHoursWithWorkLog.toFixed(1)}t)
                        </span>}
                    </button>
                  </motion.div>}
              </AnimatePresence>

              {/* MATERIALS SECTION */}
              <AnimatePresence data-id="element-1987">
                {expandedSections.materials ? <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-1988">
                    <div data-id="element-1989">
                      <button type="button" onClick={() => setExpandedSections({
                    ...expandedSections,
                    materials: false
                  })} className="flex items-center justify-between w-full mb-4 group" disabled={isReadOnly} data-id="element-1990">
                        <h3 className="text-lg font-semibold text-stone-900" data-id="element-1991">
                          Materialer
                        </h3>
                        <div className="flex items-center gap-2" data-id="element-1992">
                          {materials.length > 0 && <span className="text-sm text-stone-500" data-id="element-1993">
                              {materials.length} materialer
                            </span>}
                          <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-1994" />
                        </div>
                      </button>

                      <div className="space-y-3" data-id="element-1995">
                        {!isReadOnly && <>
                            <div className="flex items-center justify-between mb-4" data-id="element-1996">
                              <div className="flex-1" data-id="element-1997" />
                              <button type="button" onClick={() => setBulkMode(!bulkMode)} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors" data-id="element-1998">
                                <ListIcon className="w-4 h-4" data-id="element-1999" />
                                {bulkMode ? 'Enkelt tilføjelse' : 'Tilføj ordre'}
                              </button>
                            </div>

                            <AnimatePresence mode="wait" data-id="element-2000">
                              {bulkMode ? <motion.div key="bulk" initial={{
                          opacity: 0,
                          y: -10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} exit={{
                          opacity: 0,
                          y: -10
                        }} transition={{
                          duration: 0.2
                        }} className="space-y-3" data-id="element-2001">
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-2002">
                                    <p className="text-sm text-blue-900 font-medium mb-1" data-id="element-2003">
                                      📦 Tilføj stor ordre
                                    </p>
                                    <p className="text-xs text-blue-700" data-id="element-2004">
                                      Skriv ét materiale per linje. Prisen
                                      lægges automatisk til materialer pris.
                                    </p>
                                  </div>

                                  <textarea value={bulkInput} onChange={e => setBulkInput(e.target.value)} placeholder="Kabel 10m&#10;Stikkontakt&#10;Sikring 16A&#10;Kabelkanal 2m&#10;..." rows={6} className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none font-mono text-sm" data-id="element-2005" />

                                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-2006">
                                    <label className="block text-sm font-medium text-amber-900 mb-2" data-id="element-2007">
                                      💰 Pris for denne ordre (kr)
                                    </label>
                                    <input type="number" step="0.01" value={bulkPrice} onChange={e => setBulkPrice(e.target.value)} placeholder="f.eks. 2500" className="w-full px-4 py-2.5 rounded-lg border border-amber-300 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all" data-id="element-2008" />
                                    {bulkPrice && materialPrice && <div className="mt-3 pt-3 border-t border-amber-200" data-id="element-2009">
                                        <div className="flex items-center justify-between text-sm" data-id="element-2010">
                                          <span className="text-amber-700" data-id="element-2011">
                                            Nuværende materialer pris:
                                          </span>
                                          <span className="font-medium text-amber-900" data-id="element-2012">
                                            {parseFloat(materialPrice).toLocaleString('da-DK', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}{' '}
                                            kr
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm mt-1" data-id="element-2013">
                                          <span className="text-amber-700" data-id="element-2014">
                                            Ordre pris:
                                          </span>
                                          <span className="font-medium text-amber-900" data-id="element-2015">
                                            +
                                            {parseFloat(bulkPrice).toLocaleString('da-DK', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}{' '}
                                            kr
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between text-base mt-2 pt-2 border-t border-amber-300" data-id="element-2016">
                                          <span className="font-semibold text-amber-900" data-id="element-2017">
                                            Ny total:
                                          </span>
                                          <span className="font-bold text-amber-900" data-id="element-2018">
                                            {(parseFloat(materialPrice) + parseFloat(bulkPrice)).toLocaleString('da-DK', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}{' '}
                                            kr
                                          </span>
                                        </div>
                                      </div>}
                                  </div>

                                  <div className="flex gap-2" data-id="element-2019">
                                    <Button type="button" onClick={handleAddBulkMaterials} variant="primary" className="flex-1 flex items-center justify-center gap-2" disabled={!bulkInput.trim()} data-id="element-2020">
                                      <PlusIcon className="w-4 h-4" data-id="element-2021" />
                                      Tilføj alle materialer
                                      {bulkPrice && ` (+${parseFloat(bulkPrice).toLocaleString('da-DK')} kr)`}
                                    </Button>
                                    <Button type="button" onClick={() => {
                              setBulkInput('');
                              setBulkPrice('');
                              setBulkMode(false);
                            }} variant="secondary" data-id="element-2022">
                                      Annuller
                                    </Button>
                                  </div>
                                </motion.div> : <motion.div key="single" initial={{
                          opacity: 0,
                          y: -10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} exit={{
                          opacity: 0,
                          y: -10
                        }} transition={{
                          duration: 0.2
                        }} className="space-y-3" data-id="element-2023">
                                  <div className="flex gap-2" data-id="element-2024">
                                    <input type="text" value={materialInput} onChange={e => setMaterialInput(e.target.value)} onKeyPress={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddMaterial();
                              }
                            }} placeholder="Skriv materiale navn..." className="flex-1 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-2025" />
                                    <input type="number" step="0.01" value={materialPriceInput} onChange={e => setMaterialPriceInput(e.target.value)} onKeyPress={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddMaterial();
                              }
                            }} placeholder="Pris (kr)" className="w-32 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-2026" />
                                    <Button type="button" onClick={handleAddMaterial} variant="primary" className="flex items-center gap-2" disabled={!materialInput.trim()} data-id="element-2027">
                                      <PlusIcon className="w-4 h-4" data-id="element-2028" />
                                      Tilføj
                                    </Button>
                                  </div>

                                  {materialPriceInput && materialPrice && <motion.div initial={{
                            opacity: 0,
                            height: 0
                          }} animate={{
                            opacity: 1,
                            height: 'auto'
                          }} exit={{
                            opacity: 0,
                            height: 0
                          }} transition={{
                            duration: 0.2
                          }} className="bg-green-50 border border-green-200 rounded-lg p-3" data-id="element-2029">
                                      <div className="flex items-center justify-between text-sm" data-id="element-2030">
                                        <span className="text-green-700" data-id="element-2031">
                                          Nuværende total:
                                        </span>
                                        <span className="font-medium text-green-900" data-id="element-2032">
                                          {parseFloat(materialPrice).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between text-sm mt-1" data-id="element-2033">
                                        <span className="text-green-700" data-id="element-2034">
                                          Tilføjes:
                                        </span>
                                        <span className="font-medium text-green-900" data-id="element-2035">
                                          +
                                          {parseFloat(materialPriceInput).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-green-300" data-id="element-2036">
                                        <span className="font-semibold text-green-900" data-id="element-2037">
                                          Ny total:
                                        </span>
                                        <span className="font-bold text-green-900" data-id="element-2038">
                                          {(parseFloat(materialPrice) + parseFloat(materialPriceInput)).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                    </motion.div>}
                                </motion.div>}
                            </AnimatePresence>
                          </>}

                        {materials.length > 0 && <div className="space-y-2 mt-4" data-id="element-2039">
                            <Button type="button" onClick={() => setShowMaterials(!showMaterials)} variant="secondary" className="flex items-center gap-2 w-full justify-center" data-id="element-2040">
                              {showMaterials ? <>
                                  <EyeOffIcon className="w-4 h-4" data-id="element-2041" />
                                  Skjul materialer ({materials.length})
                                </> : <>
                                  <EyeIcon className="w-4 h-4" data-id="element-2042" />
                                  Se materialer ({materials.length})
                                </>}
                            </Button>

                            <AnimatePresence data-id="element-2043">
                              {showMaterials && <motion.div initial={{
                          opacity: 0,
                          height: 0
                        }} animate={{
                          opacity: 1,
                          height: 'auto'
                        }} exit={{
                          opacity: 0,
                          height: 0
                        }} transition={{
                          duration: 0.2
                        }} className="bg-stone-50 rounded-lg p-4 border border-stone-200" data-id="element-2044">
                                  <ul className="space-y-2 max-h-60 overflow-y-auto" data-id="element-2045">
                                    {materials.map((material, index) => <motion.li key={index} initial={{
                              opacity: 0,
                              x: -10
                            }} animate={{
                              opacity: 1,
                              x: 0
                            }} exit={{
                              opacity: 0,
                              x: -10
                            }} transition={{
                              delay: index * 0.02
                            }} className="bg-white p-3 rounded-lg border border-stone-200" data-id="element-2046">
                                        {editingMaterialIndex === index ? <div className="flex items-center gap-2" data-id="element-2047">
                                            <input type="text" value={editingMaterialValue} onChange={e => setEditingMaterialValue(e.target.value)} onKeyPress={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSaveEditMaterial();
                                  } else if (e.key === 'Escape') {
                                    handleCancelEditMaterial();
                                  }
                                }} className="flex-1 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" autoFocus data-id="element-2048" />
                                            <button type="button" onClick={handleSaveEditMaterial} className="p-1.5 text-teal-600 hover:bg-teal-50 rounded transition-colors" data-id="element-2049">
                                              <CheckIcon className="w-4 h-4" data-id="element-2050" />
                                            </button>
                                            <button type="button" onClick={handleCancelEditMaterial} className="p-1.5 text-stone-400 hover:bg-stone-100 rounded transition-colors" data-id="element-2051">
                                              <XCircle className="w-4 h-4" data-id="element-2052" />
                                            </button>
                                          </div> : <div className="flex items-center justify-between" data-id="element-2053">
                                            <span className="text-sm text-stone-900" data-id="element-2054">
                                              {material}
                                            </span>
                                            {!isReadOnly && <div className="flex items-center gap-1" data-id="element-2055">
                                                <button type="button" onClick={() => handleStartEditMaterial(index)} className="p-1 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors" aria-label="Rediger materiale" data-id="element-2056">
                                                  <EditIcon className="w-4 h-4" data-id="element-2057" />
                                                </button>
                                                <button type="button" onClick={() => handleRemoveMaterial(index)} className="p-1 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" aria-label="Fjern materiale" data-id="element-2058">
                                                  <XCircle className="w-4 h-4" data-id="element-2059" />
                                                </button>
                                              </div>}
                                          </div>}
                                      </motion.li>)}
                                  </ul>
                                </motion.div>}
                            </AnimatePresence>
                          </div>}

                        {materials.length === 0 && !bulkMode && <p className="text-sm text-stone-500 text-center py-4 bg-stone-50 rounded-lg border border-stone-200 mt-3" data-id="element-2060">
                            Ingen materialer tilføjet endnu
                          </p>}
                      </div>
                    </div>
                  </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-2061">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  materials: true
                })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-2062">
                      <ChevronDownIcon className="w-4 h-4" data-id="element-2063" />
                      Vis Materialer
                      {materials.length > 0 && <span className="text-stone-500" data-id="element-2064">
                          ({materials.length} materialer)
                        </span>}
                    </button>
                  </motion.div>}
              </AnimatePresence>

              {/* WORK LOG SECTION */}
              <AnimatePresence data-id="element-2065">
                {expandedSections.workLog ? <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-2066">
                    <div data-id="element-2067">
                      <button type="button" onClick={() => setExpandedSections({
                    ...expandedSections,
                    workLog: false
                  })} className="flex items-center justify-between w-full mb-4 group" disabled={isReadOnly} data-id="element-2068">
                        <h3 className="text-lg font-semibold text-stone-900" data-id="element-2069">
                          Arbejdslog
                        </h3>
                        <div className="flex items-center gap-2" data-id="element-2070">
                          {workLog.length > 0 && <span className="text-sm text-stone-500" data-id="element-2071">
                              {workLog.length} entries
                            </span>}
                          <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-2072" />
                        </div>
                      </button>

                      <div className="space-y-3" data-id="element-2073">
                        {!isReadOnly && <div className="space-y-3" data-id="element-2074">
                            <div className="flex gap-2" data-id="element-2075">
                              <input type="date" value={workLogDate} onChange={e => setWorkLogDate(e.target.value)} className="w-40 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-2076" />
                              <input type="number" step="0.5" value={workLogHours} onChange={e => setWorkLogHours(e.target.value)} placeholder="Timer" className="w-24 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-2077" />
                            </div>
                            <textarea value={workLogDescription} onChange={e => setWorkLogDescription(e.target.value)} placeholder="Beskriv hvad der blev lavet...&#10;Du kan skrive flere linjer her." rows={4} className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none" data-id="element-2078" />
                            <Button type="button" onClick={handleAddWorkLog} variant="primary" className="flex items-center gap-2 w-full justify-center" disabled={!workLogDescription.trim()} data-id="element-2079">
                              <PlusIcon className="w-4 h-4" data-id="element-2080" />
                              Tilføj til arbejdslog
                            </Button>
                          </div>}

                        {workLog.length > 0 && <div className="space-y-2 mt-4" data-id="element-2081">
                            <Button type="button" onClick={() => setShowWorkLog(!showWorkLog)} variant="secondary" className="flex items-center gap-2 w-full justify-center" data-id="element-2082">
                              {showWorkLog ? <>
                                  <EyeOffIcon className="w-4 h-4" data-id="element-2083" />
                                  Skjul arbejdslog ({workLog.length})
                                </> : <>
                                  <FileTextIcon className="w-4 h-4" data-id="element-2084" />
                                  Se arbejdslog ({workLog.length})
                                </>}
                            </Button>

                            <AnimatePresence data-id="element-2085">
                              {showWorkLog && <motion.div initial={{
                          opacity: 0,
                          height: 0
                        }} animate={{
                          opacity: 1,
                          height: 'auto'
                        }} exit={{
                          opacity: 0,
                          height: 0
                        }} transition={{
                          duration: 0.2
                        }} className="bg-stone-50 rounded-lg p-4 border border-stone-200" data-id="element-2086">
                                  <ul className="space-y-2 max-h-60 overflow-y-auto" data-id="element-2087">
                                    {workLog.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((entry, index) => {
                              const originalIndex = workLog.findIndex(e => e === entry);
                              return <motion.li key={originalIndex} initial={{
                                opacity: 0,
                                x: -10
                              }} animate={{
                                opacity: 1,
                                x: 0
                              }} exit={{
                                opacity: 0,
                                x: -10
                              }} transition={{
                                delay: index * 0.02
                              }} className="bg-white p-3 rounded-lg border border-stone-200" data-id="element-2088">
                                            {editingWorkLogIndex === originalIndex ? <div className="space-y-2" data-id="element-2089">
                                                <div className="flex gap-2" data-id="element-2090">
                                                  <input type="date" value={editingWorkLogDate} onChange={e => setEditingWorkLogDate(e.target.value)} className="w-40 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-2091" />
                                                  <input type="number" step="0.5" value={editingWorkLogHours} onChange={e => setEditingWorkLogHours(e.target.value)} placeholder="Timer" className="w-24 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-2092" />
                                                </div>
                                                <textarea value={editingWorkLogDescription} onChange={e => setEditingWorkLogDescription(e.target.value)} rows={3} className="w-full px-3 py-2 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" autoFocus data-id="element-2093" />
                                                <div className="flex gap-2" data-id="element-2094">
                                                  <button type="button" onClick={handleSaveEditWorkLog} className="flex-1 px-3 py-1.5 text-sm text-white bg-teal-600 hover:bg-teal-700 rounded transition-colors" data-id="element-2095">
                                                    Gem
                                                  </button>
                                                  <button type="button" onClick={handleCancelEditWorkLog} className="flex-1 px-3 py-1.5 text-sm text-stone-600 bg-stone-100 hover:bg-stone-200 rounded transition-colors" data-id="element-2096">
                                                    Annuller
                                                  </button>
                                                </div>
                                              </div> : <div className="flex items-start justify-between gap-3" data-id="element-2097">
                                                <div className="flex-1" data-id="element-2098">
                                                  <div className="flex items-center gap-2 mb-1" data-id="element-2099">
                                                    <CalendarIcon className="w-3.5 h-3.5 text-teal-600" data-id="element-2100" />
                                                    <span className="text-xs font-medium text-teal-700" data-id="element-2101">
                                                      {new Date(entry.date).toLocaleDateString('da-DK', {
                                          weekday: 'short',
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric'
                                        })}
                                                    </span>
                                                    {entry.hours && <>
                                                        <span className="text-xs text-stone-400" data-id="element-2102">
                                                          •
                                                        </span>
                                                        <ClockIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-2103" />
                                                        <span className="text-xs font-medium text-blue-700" data-id="element-2104">
                                                          {entry.hours}t
                                                        </span>
                                                      </>}
                                                  </div>
                                                  <p className="text-sm text-stone-900" data-id="element-2105">
                                                    {entry.description}
                                                  </p>
                                                </div>
                                                {!isReadOnly && <div className="flex items-center gap-1 flex-shrink-0" data-id="element-2106">
                                                    <button type="button" onClick={() => handleStartEditWorkLog(originalIndex)} className="p-1 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors" aria-label="Rediger log" data-id="element-2107">
                                                      <EditIcon className="w-4 h-4" data-id="element-2108" />
                                                    </button>
                                                    <button type="button" onClick={() => handleRemoveWorkLog(originalIndex)} className="p-1 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" aria-label="Fjern log" data-id="element-2109">
                                                      <XCircle className="w-4 h-4" data-id="element-2110" />
                                                    </button>
                                                  </div>}
                                              </div>}
                                          </motion.li>;
                            })}
                                  </ul>
                                </motion.div>}
                            </AnimatePresence>
                          </div>}
                      </div>
                    </div>
                  </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-2111">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  workLog: true
                })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-2112">
                      <ChevronDownIcon className="w-4 h-4" data-id="element-2113" />
                      Vis Arbejdslog
                      {workLog.length > 0 && <span className="text-stone-500" data-id="element-2114">
                          ({workLog.length} entries)
                        </span>}
                    </button>
                  </motion.div>}
              </AnimatePresence>

              {/* ATTACHMENTS SECTION */}
              <AnimatePresence data-id="element-2115">
                {expandedSections.attachments ? <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-2116">
                    <div data-id="element-2117">
                      <button type="button" onClick={() => setExpandedSections({
                    ...expandedSections,
                    attachments: false
                  })} className="flex items-center justify-between w-full mb-4 group" disabled={isReadOnly} data-id="element-2118">
                        <h3 className="text-lg font-semibold text-stone-900" data-id="element-2119">
                          Bilag
                        </h3>
                        <div className="flex items-center gap-2" data-id="element-2120">
                          {attachments.length > 0 && <span className="text-sm text-stone-500" data-id="element-2121">
                              {attachments.length} bilag
                            </span>}
                          <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-2122" />
                        </div>
                      </button>

                      <div className="space-y-3" data-id="element-2123">
                        {!isReadOnly && <div className="flex justify-end mb-4" data-id="element-2124">
                            <input type="file" id="file-upload" className="hidden" onChange={handleFileUpload} disabled={isUploading} data-id="element-2125" />
                            <label htmlFor="file-upload" className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors cursor-pointer ${isUploading ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-2126">
                              {isUploading ? <>
                                  <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-2127" />
                                  Uploader...
                                </> : <>
                                  <UploadIcon className="w-4 h-4" data-id="element-2128" />
                                  Tilføj bilag
                                </>}
                            </label>
                          </div>}

                        {attachments.length > 0 ? <ul className="space-y-2" data-id="element-2129">
                            {attachments.map((attachment, index) => <motion.li key={attachment.storagePath} initial={{
                        opacity: 0,
                        y: -10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} className="flex items-center justify-between bg-white p-3 rounded-lg border border-stone-200" data-id="element-2130">
                                <div className="flex items-center gap-3 overflow-hidden" data-id="element-2131">
                                  <div className="p-2 bg-stone-100 rounded-lg flex-shrink-0" data-id="element-2132">
                                    <FileIcon className="w-5 h-5 text-stone-600" data-id="element-2133" />
                                  </div>
                                  <div className="min-w-0" data-id="element-2134">
                                    <p className="text-sm font-medium text-stone-900 truncate" data-id="element-2135">
                                      {attachment.name}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs text-stone-500" data-id="element-2136">
                                      <span data-id="element-2137">
                                        {new Date(attachment.uploadedAt).toLocaleDateString('da-DK', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                                      </span>
                                      {attachment.size && <>
                                          <span data-id="element-2138">•</span>
                                          <span data-id="element-2139">
                                            {formatFileSize(attachment.size)}
                                          </span>
                                        </>}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 flex-shrink-0 ml-4" data-id="element-2140">
                                  <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="p-2 text-stone-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Download" data-id="element-2141">
                                    <DownloadIcon className="w-4 h-4" data-id="element-2142" />
                                  </a>
                                  {!isReadOnly && <button type="button" onClick={() => handleDeleteAttachment(attachment, index)} className="p-2 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Slet" data-id="element-2143">
                                      <Trash2Icon className="w-4 h-4" data-id="element-2144" />
                                    </button>}
                                </div>
                              </motion.li>)}
                          </ul> : <div className="text-center py-8 bg-stone-50 rounded-lg border border-stone-200" data-id="element-2145">
                            <PaperclipIcon className="w-8 h-8 text-stone-400 mx-auto mb-2" data-id="element-2146" />
                            <p className="text-sm text-stone-500" data-id="element-2147">
                              Ingen bilag tilføjet endnu
                            </p>
                          </div>}
                      </div>
                    </div>
                  </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-2148">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  attachments: true
                })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-2149">
                      <ChevronDownIcon className="w-4 h-4" data-id="element-2150" />
                      Vis Bilag
                      {attachments.length > 0 && <span className="text-stone-500" data-id="element-2151">
                          ({attachments.length} bilag)
                        </span>}
                    </button>
                  </motion.div>}
              </AnimatePresence>

              {/* Vigtige bemærkninger */}
              <div data-id="element-2152">
                <RichTextEditor label="Vigtige bemærkninger" value={subTasks} onChange={setSubTasks} placeholder="Vigtige bemærkninger om opgaven" rows={3} disabled={isReadOnly} data-id="element-2153" />
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2154">
                {/* Fortjeneste */}
                {calculations.profit !== null && <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg" data-id="element-2155">
                    <div className="flex items-center justify-between" data-id="element-2156">
                      <span className="text-sm font-medium text-teal-900" data-id="element-2157">
                        Fortjeneste
                      </span>
                      <span className={`text-lg font-bold ${calculations.profit >= 0 ? 'text-teal-700' : 'text-red-700'}`} data-id="element-2158">
                        {calculations.profit.toLocaleString('da-DK', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}{' '}
                        kr
                      </span>
                    </div>
                  </div>}

                {/* Timer brugt i alt */}
                {totalHoursWithWorkLog > 0 && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-2159">
                    <div className="flex items-center justify-between" data-id="element-2160">
                      <span className="text-sm font-medium text-blue-900" data-id="element-2161">
                        Timer brugt i alt
                      </span>
                      <span className="text-lg font-bold text-blue-700" data-id="element-2162">
                        {totalHoursWithWorkLog.toFixed(1)} timer
                      </span>
                    </div>
                  </div>}
              </div>
            </form>
          </div>

          {/* Sticky bottom buttons */}
          <div className="sticky bottom-0 z-10 bg-white border-t border-stone-200 rounded-b-2xl px-4 sm:px-6 py-4" data-id="element-2163">
            <div className="flex gap-3" data-id="element-2164">
              <Button type="button" variant="secondary" onClick={onBack} className="flex-1" data-id="element-2165">
                Annuller
              </Button>
              <Button type="button" variant="primary" onClick={handleSubmit as any} className="flex-1" disabled={isReadOnly} data-id="element-2166">
                Gem ændringer
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
}